import React, { useCallback, useMemo, useRef } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { LeaveStatusBadge } from '../../components/LeaveStatusBadge';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { getLeaveModuleCapabilities } from '../../config/leaveCapabilities';
import type { ColorPalette } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useLeaveApplications } from '../../hooks/useLeaveApplications';
import type { LeaveApplicationListItem } from '../../types/leave';
import { createDefaultLeaveFilters } from '../../utils/leaveFilters';
import {
  formatCalendarDate,
} from '../../utils/dateTime';
import { formatLeaveApplicationDuration } from '../../utils/leaveSession';
import { formatDisplayLabel } from '../../utils/camelCaseLabel';

type Props = NativeStackScreenProps<RootStackParamList, 'LeaveApplications'>;

function LeaveApplicationRow({
  item,
  onPress,
  palette,
}: {
  item: LeaveApplicationListItem;
  onPress?: () => void;
  palette: ColorPalette;
}) {
  const leaveType = formatDisplayLabel(
    item.leaveTypeName,
    'Leave type unavailable',
  );
  const duration = formatLeaveApplicationDuration(
    item.totalDays,
    item.sessionType,
    [],
    item.sessionTypeName,
  );
  const dateRange = `${formatCalendarDate(item.leaveFromDate)} - ${formatCalendarDate(item.leaveToDate)}`;
  const content = (
    <>
      <View style={styles.rowTop}>
        <Text style={[styles.title, { color: palette.textPrimary }]}>
          {leaveType}
        </Text>
        <Text style={[styles.duration, { color: palette.textPrimary }]}>
          {duration}
        </Text>
      </View>
      <View style={styles.rowBottom}>
        <Text style={[styles.meta, { color: palette.textSecondary }]}>
          {dateRange}
        </Text>
        <LeaveStatusBadge
          label={item.statusLabel}
          statusCode={item.statusCode}
        />
      </View>
    </>
  );

  if (!onPress) {
    return (
      <View
        accessibilityRole="text"
        accessibilityLabel={`${leaveType}, ${duration}, status ${item.statusLabel}`}
        style={[styles.row, { borderBottomColor: palette.border }]}
      >
        {content}
      </View>
    );
  }

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${leaveType}, ${duration}, status ${item.statusLabel}`}
      onPress={onPress}
      style={[styles.row, { borderBottomColor: palette.border }]}
    >
      {content}
    </Pressable>
  );
}

export function LeaveApplicationListScreen({ navigation, route }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const capabilities = getLeaveModuleCapabilities(auth.permissions);
  const history = useLeaveApplications();
  const {
    applyFilters,
    loadApplications,
    refreshApplications,
    resetFilters,
    retry,
    filters,
    items,
    isLoading,
    isRefreshing,
    isLoadingMore,
    error,
    loadMore,
  } = history;
  const defaults = useMemo(() => createDefaultLeaveFilters(), []);
  const hasFocused = useRef(false);

  const themed = useMemo(
    () => ({
      range: {
        ...typography.body,
        color: palette.textSecondary,
        marginBottom: spacing.md,
      },
      warning: {
        ...typography.caption,
        color: palette.warning,
        marginBottom: spacing.md,
      },
    }),
    [palette],
  );

  useFocusEffect(
    useCallback(() => {
      const applied = route.params?.appliedFilters;
      if (applied) {
        applyFilters(applied);
        navigation.setParams({ appliedFilters: undefined });
        hasFocused.current = true;
        return;
      }
      if (!hasFocused.current) {
        hasFocused.current = true;
        loadApplications();
        return;
      }
      refreshApplications();
    }, [
      applyFilters,
      loadApplications,
      navigation,
      refreshApplications,
      route.params?.appliedFilters,
    ]),
  );

  const filtersCustomized =
    filters.fromDate !== defaults.fromDate ||
    filters.toDate !== defaults.toDate;

  const footer = useMemo(() => {
    if (!isLoadingMore) return null;
    return (
      <View
        style={styles.footer}
        accessibilityLabel="Loading more leave applications"
      >
        <ActivityIndicator color={palette.primary} />
      </View>
    );
  }, [isLoadingMore, palette.primary]);

  return (
    <PermissionGuard
      permission={PERMISSIONS.LEAVE_APPLY}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen contentStyle={styles.screen}>
        <ScreenHeader
          title="Leave Applications"
          onBack={navigation.goBack}
        />
        <View style={styles.body}>
          <Text style={themed.range}>
            {filters.fromDate} to {filters.toDate}
          </Text>
          <View style={styles.actions}>
            <AppButton
              title="Filters"
              variant="secondary"
              onPress={() =>
                navigation.navigate(routes.leaveFilter, {
                  currentFilters: filters,
                })
              }
            />
          </View>
          {!capabilities.canApply ? (
            <Text style={themed.warning}>
              Applying for leave is not available in this app yet.
            </Text>
          ) : null}

          {isLoading && items.length === 0 ? (
            <View style={styles.center}>
              <ActivityIndicator
                color={palette.primary}
                accessibilityLabel="Loading leave applications"
              />
            </View>
          ) : error && items.length === 0 ? (
            <ErrorState message={error} onRetry={retry} />
          ) : (
            <FlatList
              data={items}
              keyExtractor={item => String(item.leaveApplicationId)}
              refreshControl={
                <RefreshControl
                  refreshing={isRefreshing}
                  onRefresh={refreshApplications}
                />
              }
              onEndReached={loadMore}
              onEndReachedThreshold={0.4}
              ListFooterComponent={footer}
              ListEmptyComponent={
                <EmptyState
                  title="No leave applications"
                  message="No leave applications were found for this period."
                  actionTitle={filtersCustomized ? 'Reset filters' : 'Retry'}
                  onAction={filtersCustomized ? resetFilters : retry}
                />
              }
              contentContainerStyle={styles.listContent}
              renderItem={({ item }) => (
                <LeaveApplicationRow
                  item={item}
                  palette={palette}
                  onPress={
                    capabilities.canViewDetails
                      ? () =>
                          navigation.navigate(routes.leaveDetails, {
                            leaveApplicationId: item.leaveApplicationId,
                            applicationNumber: item.applicationNumber,
                            leaveTypeName: item.leaveTypeName,
                            leaveFromDate: item.leaveFromDate,
                            leaveToDate: item.leaveToDate,
                            totalDays: item.totalDays,
                            sessionType: item.sessionType,
                            sessionTypeName: item.sessionTypeName,
                            statusCode: item.statusCode,
                            applicationDate: item.applicationDate,
                          })
                      : undefined
                  }
                />
              )}
            />
          )}
        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, paddingBottom: 0 },
  body: { flex: 1, marginTop: spacing.lg },
  actions: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  listContent: { flexGrow: 1, paddingBottom: spacing.xl },
  row: {
    paddingVertical: spacing.lg,
    borderBottomWidth: StyleSheet.hairlineWidth,
    gap: spacing.sm,
  },
  rowTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: spacing.sm,
  },
  rowBottom: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: spacing.sm,
  },
  title: { ...typography.body, fontWeight: '700', flex: 1 },
  duration: { ...typography.body, fontWeight: '600' },
  meta: { ...typography.caption, flex: 1 },
  footer: { paddingVertical: spacing.lg, alignItems: 'center' },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
});
